import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface TokenUsageChartData {
  date: string;
  [modelKey: string]: string | number | undefined;
}

interface TokenUsageChartProps {
  data: TokenUsageChartData[];
}

const TokenUsageChart: React.FC<TokenUsageChartProps> = ({ data }) => {
  const modelKeys = useMemo(() => {
    if (!data || data.length === 0) return [];
    const keys = Object.keys(data[0]).filter(key => 
      key !== 'date' && !key.endsWith('Input') && !key.endsWith('Output')
    );
    return keys.sort();
  }, [data]);

  const getProviderFromModelKey = (modelKey: string): string => {
    const parts = modelKey.split('-');
    return parts[0] || 'unknown';
  };

  const getModelName = (modelKey: string): string => {
    const parts = modelKey.split('-');
    if (parts.length > 1) {
      return parts.slice(1).join('-');
    }
    return modelKey;
  };

  const getProviderColor = (provider: string): string => {
    const providerColors: { [key: string]: string } = {
      claude: '#333333',
      gemini: '#28a745',
      grok: '#007bff',
      openai: '#666666'
    };
    return providerColors[provider.toLowerCase()] || '#666666';
  };

  const getModelColor = (modelKey: string, index: number): string => {
    const provider = getProviderFromModelKey(modelKey);
    const baseColor = getProviderColor(provider);
    
    if (baseColor === '#333333') {
      const shades = ['#333333', '#555555', '#777777'];
      return shades[index % shades.length];
    } else if (baseColor === '#28a745') {
      const shades = ['#28a745', '#20c997', '#17a2b8'];
      return shades[index % shades.length];
    } else if (baseColor === '#007bff') {
      const shades = ['#007bff', '#6f42c1', '#e83e8c'];
      return shades[index % shades.length];
    } else {
      const shades = ['#666666', '#999999', '#bbbbbb'];
      return shades[index % shades.length];
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const payloadData = payload[0].payload;
      return (
        <div className="dashboard-chart-tooltip">
          <p className="dashboard-chart-tooltip-label">{payloadData.date}</p>
          {payload.map((entry: any, index: number) => {
            const modelKey = entry.dataKey;
            const totalTokens = entry.value;
            const inputTokens = payloadData[`${modelKey}Input`] || 0;
            const outputTokens = payloadData[`${modelKey}Output`] || 0;
            
            if (totalTokens === 0) return null;
            
            const provider = getProviderFromModelKey(modelKey);
            const modelName = getModelName(modelKey);
            const displayName = `${provider.charAt(0).toUpperCase() + provider.slice(1)} ${modelName}`;
            
            return (
              <div key={index}>
                <p className="dashboard-chart-tooltip-value" style={{ color: entry.color, fontWeight: 500 }}>
                  {displayName}: {totalTokens.toLocaleString('en-US')} tokens
                </p>
                {(inputTokens > 0 || outputTokens > 0) && (
                  <p className="dashboard-chart-tooltip-value" style={{ color: entry.color, fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                    Input: {inputTokens.toLocaleString('en-US')} | Output: {outputTokens.toLocaleString('en-US')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-chart-wrapper">
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={80}
            tick={{ fill: '#666666', fontSize: 12 }}
          />
          <YAxis
            tick={{ fill: '#666666', fontSize: 12 }}
            label={{ value: 'Tokens', angle: -90, position: 'insideLeft', fill: '#666666' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {modelKeys.map((modelKey, index) => {
            const provider = getProviderFromModelKey(modelKey);
            const modelName = getModelName(modelKey);
            const displayName = `${provider.charAt(0).toUpperCase() + provider.slice(1)} ${modelName}`;
            const color = getModelColor(modelKey, index);
            
            return (
              <Line 
                key={modelKey}
                type="monotone" 
                dataKey={modelKey} 
                stroke={color}
                strokeWidth={2}
                name={displayName}
                dot={{ fill: color, r: 4 }}
                activeDot={{ r: 6 }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TokenUsageChart;

